import { memo, useCallback, useMemo } from 'react'
import type { KeyboardEvent, MouseEvent, SyntheticEvent } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import { CurrencyProvider } from 'contexts/currencyContext'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { setCSSVar } from 'lib/theme/themeUtils'
import focusStyle from 'styles/utils/focusStyle'
import { last } from 'lib/array/arrayUtils'
import { getArrivalDayOffset, getTotalStopCount } from 'lib/flights/flightUtils'

interface Props {
  journey: App.JourneyV2
  onSelect?: (data: { journey: App.JourneyV2 }) => void
  onViewDetails?: () => void
  isAlreadyAdded?: boolean
  isSelectDisabled?: boolean
  selectLabel?: string
  showSelectButton?: boolean
  showPrice?: boolean
}

/**
 * Width at which the card switches between the two variants of `💠 Card_Flight`
 * in Trip Kit 3.0. The `Device=Desktop` variant lays every column out on one
 * row, which needs roughly 600px of card before the columns start to compress —
 * below that we render the stacked `Device=Mobile` variant instead.
 */
const DESKTOP_CARD_BREAKPOINT = 600

const desktopCard = `@container chat-flight-card (min-width: ${rem(DESKTOP_CARD_BREAKPOINT)})`

/**
 * Figma pins the card's headings to `Font/Size/H6` (a flat 16/19), but LuxKit's
 * `heading6` steps down to 14px below the tablet breakpoint. Both LuxKit rules
 * are `.hash.variant-heading6`, so an override needs to beat two classes.
 */
const figmaHeading6 = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '19px')}
  font-size: 16px;
`

const CardQueryContainer = styled.div`
  container-name: chat-flight-card;
  container-type: inline-size;
  width: 100%;
`

const CardShell = styled(Sheet)<{ $interactive: boolean }>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(12)};
  width: 100%;

  ${desktopCard} {
    align-items: center;
    column-gap: ${rem(24)};
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: ${rem(16)} ${rem(12)};
    row-gap: ${rem(16)};
  }

  ${props => props.$interactive && css`
    cursor: pointer;

    &:focus-visible {
      ${focusStyle}
    }

    ${mediaHoverable} {
      &&:hover {
        border-color: ${props.theme.palette.neutral.default.four};
      }
    }
  `}
`

const FlightSummary = styled.div`
  align-items: center;
  column-gap: ${rem(8)};
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  row-gap: ${rem(8)};
  width: 100%;

  ${desktopCard} {
    column-gap: ${rem(24)};
    justify-content: flex-start;
    width: auto;
  }
`

const AirlineGroup = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(4)};
  min-width: 0;

  ${desktopCard} {
    gap: ${rem(8)};
  }
`

const AirlineLogoBox = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${rem(32)};
  justify-content: center;
  width: ${rem(32)};

  ${desktopCard} {
    height: ${rem(38)};
    width: ${rem(38)};
  }
`

const AirlineLogoImage = styled.img`
  height: ${rem(27)};
  object-fit: contain;
  width: ${rem(27)};

  ${desktopCard} {
    height: ${rem(32)};
    width: ${rem(32)};
  }
`

const StackedColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  justify-content: center;
  min-width: 0;
`

const FlightTimeRow = styled.div`
  align-items: baseline;
  display: flex;
  white-space: nowrap;
`

const FlightTimeRange = styled(Heading)`
  &&& {
    ${figmaHeading6}
  }
`

const ArrivalDayOffset = styled(Caption)`
  margin-inline-start: ${rem(4)};
`

const FlightMetaGroup = styled.div`
  display: flex;
  gap: ${rem(12)};
  min-width: 0;
  white-space: nowrap;

  ${desktopCard} {
    gap: ${rem(24)};
  }
`

const CardFooter = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(12)};
  width: 100%;

  ${desktopCard} {
    gap: ${rem(24)};
    width: auto;
  }
`

const PriceCell = styled.div`
  min-width: 0;

  /* Figma butts the sale unit straight onto the fare; LuxKit's price row adds 0.25em. */
  && > * {
    column-gap: 0;
  }

  /* See figmaHeading6 — the price point renders its own LuxKit heading6. */
  && .variant-heading6 {
    ${figmaHeading6}
  }
`

/** Figma renders the fare in the card's body colour rather than LuxKit's stronger `normal-contrast`. */
const StandardPriceCell = styled(PriceCell)`
  && .variant-heading6 {
    color: ${props => props.theme.palette.neutral.default.two};
  }
`

/** Figma models the CTA as a 100px column with the 94px chip pinned to its end. */
const ActionCell = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: center;
  margin-inline-start: auto;
  min-width: ${rem(100)};
`

function getFirstStopAirport(flights: Array<App.FlightV2>): string {
  return flights.length > 1 ?
    flights[0].arrivalAirport :
    flights[0]?.hiddenStops?.[0]?.airportCode ?? ''
}

function getStopsLabel(numberOfStops: number, intl: IntlShape): string {
  if (numberOfStops === 0) {
    return intl.formatMessage({
      id: 'Cvc6NejS9VBmrNzgU3V9',
      defaultMessage: 'Direct',
      description: 'Label indicating a non-stop direct flight in the trip planner drawer flight card',
    })
  }
  if (numberOfStops === 1) {
    return intl.formatMessage({
      id: 'gfP5C0rUG_rWKvBwZ6Oy',
      defaultMessage: '1 stop',
      description: 'Label for a flight with a single layover stop in the trip planner drawer flight card',
    })
  }
  return intl.formatMessage(
    {
      id: 'TMkiPFK3ZUXBJuoTfJvb',
      defaultMessage: '{count} stops',
      description: 'Label showing the number of layover stops in the trip planner drawer flight card',
    },
    { count: numberOfStops },
  )
}

function getLayoverDetail(numberOfStops: number, flights: Array<App.FlightV2>): string {
  if (numberOfStops === 0) return ''
  const layoverAirport = getFirstStopAirport(flights)
  if (!layoverAirport) return ''
  if (flights.length < 2) return layoverAirport
  const { hours, minutes } = flights[0].stopoverTime
  if (hours || minutes) {
    return `${hours}h ${minutes}m ${layoverAirport}`
  }
  return layoverAirport
}

function DrawerFlightCard({
  journey,
  onSelect,
  onViewDetails,
  isAlreadyAdded = false,
  isSelectDisabled = false,
  selectLabel: selectLabelOverride,
  showSelectButton = true,
  showPrice = true,
}: Props) {
  const intl = useIntl()

  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = last(flights)
  const duration = journey.flightGroup.totalFlightDuration
  const numberOfStops = getTotalStopCount(flights)

  const logoSrc = journey.carrierBrandMark ?? journey.carrierLogo
  const routeLabel = `${firstFlight.departureAirport}-${lastFlight.arrivalAirport}`

  const durationLabel = `${duration.hours}h ${duration.minutes}m`

  const arrivalDaysAhead = getArrivalDayOffset(firstFlight.departingDate, lastFlight.arrivalDate)

  const arrivalDayOffsetLabel = intl.formatMessage(
    {
      id: 'B-LRf5XhtbzyX-ftELv2',
      defaultMessage: '+{count}',
      description: 'Number of calendar days after departure that a flight arrives',
    },
    { count: arrivalDaysAhead },
  )

  const stopsLabel = useMemo(
    () => getStopsLabel(numberOfStops, intl),
    [numberOfStops, intl],
  )

  const layoverDetail = useMemo(
    () => getLayoverDetail(numberOfStops, flights),
    [numberOfStops, flights],
  )

  const defaultSelectLabel = intl.formatMessage({
    id: 'FOREyFIFyoG8zaEUykey',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a flight to the trip planner itinerary from the drawer search results',
  })

  const addedLabel = intl.formatMessage({
    id: 'yjcRGlZFznW-to4yKCRx',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label shown when a flight is already in the trip planner itinerary',
  })
  const viewDetailsLabel = intl.formatMessage(
    {
      id: 'efrN4MevEqwji_FiE7VN',
      defaultMessage: 'View flight details for {carrier}, {departureTime} to {arrivalTime}',
      description: 'Accessible label for the trip planner flight result card, which opens the flight details view when activated',
    },
    {
      carrier: journey.carrierName,
      departureTime: firstFlight.departingTime,
      arrivalTime: lastFlight.arrivalTime,
    },
  )

  const handleClick = useCallback(() => {
    if (!onSelect) return
    onSelect({ journey })
  }, [onSelect, journey])

  // Figma has no "Flight details" link on the card, so the card itself opens the
  // details view. Clicks that land on the CTA (or any other nested control) are
  // left to that control.
  const isInteractiveTarget = useCallback((event: SyntheticEvent) =>
    event.target instanceof Element &&
    didClickInteractableElement(event.target as HTMLElement, event.currentTarget as HTMLElement),
  [])

  const handleCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!onViewDetails || isInteractiveTarget(event)) return
    onViewDetails()
  }, [onViewDetails, isInteractiveTarget])

  const handleCardKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!onViewDetails || isInteractiveTarget(event)) return
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      onViewDetails()
    }
  }, [onViewDetails, isInteractiveTarget])

  const memberPrice = journey.price.adult.totalFare ?? 0
  const regularPrice = journey.regularPrice?.adult?.totalFare
  const showLuxPlusPrice = journey.bookingInfo.isLuxPlusFare === true && Number.isFinite(regularPrice)

  const priceDetails = showPrice ? (
    <CurrencyProvider currency={journey.currencyCode}>
      {showLuxPlusPrice ?
          <PriceCell>
            <PriceRowLuxPlusPrice size="S" memberPrice={memberPrice} nonMemberPrice={regularPrice} saleUnit="adult" />
          </PriceCell> :
          <StandardPriceCell>
            <PriceRowPrice size="S" price={memberPrice} saleUnit="adult" />
          </StandardPriceCell>}
    </CurrencyProvider>
  ) : null

  const selectButton = showSelectButton ? (
    <AssistChip
      variant="filled"
      size="small"
      startIcon={isAlreadyAdded ? <LineCheckIcon /> : <LinePlusIcon />}
      onClick={handleClick}
      disabled={isAlreadyAdded || isSelectDisabled}
    >
      {isAlreadyAdded ? addedLabel : (selectLabelOverride ?? defaultSelectLabel)}
    </AssistChip>
  ) : null

  const actionCell = selectButton ? <ActionCell>{selectButton}</ActionCell> : null

  return (
    <CardQueryContainer>
      <CardShell
        background="normal-background"
        border="light-foreground"
        borderRadius="L"
        $interactive={!!onViewDetails}
        role={onViewDetails ? 'button' : undefined}
        tabIndex={onViewDetails ? 0 : undefined}
        aria-label={onViewDetails ? viewDetailsLabel : undefined}
        onClick={onViewDetails ? handleCardClick : undefined}
        onKeyDown={onViewDetails ? handleCardKeyDown : undefined}
      >
        <FlightSummary>
          <AirlineGroup>
            {logoSrc && (
              <AirlineLogoBox>
                <AirlineLogoImage src={logoSrc} alt={journey.carrierName} />
              </AirlineLogoBox>
            )}
            <StackedColumn>
              <FlightTimeRow>
                <FlightTimeRange as="span" variant="heading6" colour="neutral-two">
                  {intl.formatMessage(
                    {
                      id: 'zOG3k0v44GaR0Jn64tgV',
                      defaultMessage: '{departureTime} - {arrivalTime}',
                      description: 'Flight departure and arrival time range in the trip planner flight drawer card',
                    },
                    { departureTime: firstFlight.departingTime, arrivalTime: lastFlight.arrivalTime },
                  )}
                </FlightTimeRange>
                {arrivalDaysAhead > 0 && (
                  <ArrivalDayOffset as="span" variant="medium" weight="bold" colour="neutral-two">
                    {arrivalDayOffsetLabel}
                  </ArrivalDayOffset>
                )}
              </FlightTimeRow>
              <Caption variant="medium" colour="neutral-three" wrap="no-wrap">
                {journey.carrierName}
              </Caption>
            </StackedColumn>
          </AirlineGroup>
          <FlightMetaGroup>
            <StackedColumn>
              <BodyText variant="medium" colour="neutral-two">{durationLabel}</BodyText>
              <Caption variant="medium" colour="neutral-three">{routeLabel}</Caption>
            </StackedColumn>
            <StackedColumn>
              <BodyText variant="medium" colour="neutral-two">{stopsLabel}</BodyText>
              {layoverDetail && (
                <Caption variant="medium" colour="neutral-three">{layoverDetail}</Caption>
              )}
            </StackedColumn>
          </FlightMetaGroup>
        </FlightSummary>
        {(priceDetails || actionCell) && (
          <CardFooter>
            {priceDetails}
            {actionCell}
          </CardFooter>
        )}
      </CardShell>
    </CardQueryContainer>
  )
}

export default memo(DrawerFlightCard)
